import {
  type AutocompleteRootElement,
  Autocomplete,
  FormControl,
  Input,
  useDebounceCallback,
} from '@farol-ds/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useSearchEmecLawInstitutions } from '../../hooks/use-search-emec-law-institutions'

export interface InstitutionAutocompleteProps {
  label: string
  placeholder: string
  value: string
  onInstitutionChange: (institution: string) => void
  onInstitutionSelected: (selected: boolean) => void
}

export function InstitutionAutocomplete({
  label,
  placeholder,
  value,
  onInstitutionChange,
  onInstitutionSelected,
}: InstitutionAutocompleteProps) {
  const { searchInstitutions, autocompleteOptions, loading } = useSearchEmecLawInstitutions()
  const autocompleteRef = useRef<AutocompleteRootElement>(null)
  const [inputValue, setInputValue] = useState(value)
  const [hasSelection, setHasSelection] = useState(Boolean(value))

  const debouncedSearch = useDebounceCallback((query: string) => {
    searchInstitutions(query)
  }, 300)

  useEffect(() => {
    setInputValue(value)
    setHasSelection(Boolean(value))
  }, [value])

  // Async results arrive after the menu opened with zero options; reopen when options load.
  // Skip while a selection is active, otherwise picking an option would immediately reopen the menu.
  useEffect(() => {
    if (hasSelection || inputValue.trim().length < 2 || autocompleteOptions.length === 0) {
      return
    }
    autocompleteRef.current?.menu?.setOpen(true)
  }, [autocompleteOptions, inputValue, hasSelection])

  const handleValueChange = useCallback(
    (_event: unknown, nextValue: string) => {
      setInputValue(nextValue)
      setHasSelection(false)
      onInstitutionSelected(false)
      onInstitutionChange('')

      if (nextValue.trim().length < 2) {
        debouncedSearch.cancel()
        return
      }

      debouncedSearch(nextValue)
    },
    [debouncedSearch, onInstitutionChange, onInstitutionSelected]
  )

  const handleOptionSelect = useCallback(
    (option: { value: string }) => {
      setInputValue(option.value)
      setHasSelection(true)
      onInstitutionSelected(true)
      onInstitutionChange(option.value)
      autocompleteRef.current?.menu?.setOpen(false)
    },
    [onInstitutionChange, onInstitutionSelected]
  )

  return (
    <FormControl required>
      <FormControl.Label>{label}</FormControl.Label>
      <Autocomplete ref={autocompleteRef} data-testid="institution-autocomplete">
        <Autocomplete.Trigger loading={loading}>
          <Input
            autoComplete="off"
            autoCorrect="off"
            placeholder={placeholder}
            value={inputValue}
            onValueChange={handleValueChange}
            aria-invalid={!hasSelection && inputValue.length > 0 ? true : undefined}
          />
        </Autocomplete.Trigger>
        <Autocomplete.Menu
          filterOptions={null}
          options={autocompleteOptions}
          onOptionSelect={handleOptionSelect}
        />
      </Autocomplete>
    </FormControl>
  )
}
